import React, { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  TableSortLabel,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ShoppingCart,
  Sell,
  MonetizationOn,
  Receipt,
  Search,
  Add,
  Delete,
  AccountBalanceWallet,
  FileDownload,
  Edit,
  ContentCopy,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAppContext } from '../context/AppContext';
import { useNotification } from '../context/NotificationContext';
import { logger } from '../utils/logger';
import PageHeader from './common/PageHeader';
import { StockNameManager } from '../services/stockNameManager';
import EditTransactionDialog from './EditTransactionDialog';
import { Transaction } from '../types';

type SortField = 'transactionDate' | 'symbol' | 'total' | 'quantity';
type SortDirection = 'asc' | 'desc';

const TransactionHistory: React.FC = () => {
  const { state, updateTransaction, deleteTransaction } = useAppContext();
  const { showSuccess, showError } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease'>('all');
  const [sortField, setSortField] = useState<SortField>('transactionDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // In multi-account system, state.transactions already contains transactions for current account
  const userTransactions = useMemo(() => {
    // For multi-account system, transactions are already filtered by current account
    if (state.currentAccountId) {
      return state.transactions;
    }
    
    // Fallback to legacy filtering for backward compatibility
    if (!state.currentUser) return [];
    
    return state.transactions.filter(transaction => 
      transaction.accountId === state.currentAccountId  // 使用當前交易帳戶 ID
    );
  }, [state.transactions, state.currentUser, state.currentAccountId]);

  // Apply filters and sorting
  const filteredTransactions = useMemo(() => {
    // FIN-04:一律複製後再 sort — 無篩選時 filtered 與 userTransactions 是同一引用,
    // 就地 sort 會變異 AppContext 的 state 陣列,污染全域交易順序
    let filtered = [...userTransactions];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.symbol.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(transaction => transaction.type === typeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'transactionDate') {
        // 優先使用 transactionDate，如果沒有則回退到 timestamp
        aValue = new Date(a.transactionDate || a.timestamp).getTime();
        bValue = new Date(b.transactionDate || b.timestamp).getTime();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [userTransactions, searchTerm, typeFilter, sortField, sortDirection]);

  // Calculate statistics
  const stats = useMemo(() => {
    const buyTransactions = userTransactions.filter(t => t.type === 'buy');
    const sellTransactions = userTransactions.filter(t => t.type === 'sell');
    
    const totalBought = buyTransactions.reduce((sum, t) => sum + t.total, 0);
    const totalSold = sellTransactions.reduce((sum, t) => sum + t.total, 0);
    
    return {
      totalTransactions: userTransactions.length,
      buyCount: buyTransactions.length,
      sellCount: sellTransactions.length,
      totalBought,
      totalSold,
      netAmount: totalSold - totalBought,
    };
  }, [userTransactions]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 使用統一的股票名稱管理器
  const getStockName = (symbol: string, storedName?: string) => {
    return StockNameManager.getStockName(symbol);
  };

  // 編輯交易處理函數
  const handleEditClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setEditDialogOpen(true);
  };

  const handleEditSave = async (updatedTransaction: Transaction) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      // AppContext 的 updateTransaction 期望 (transactionId, updates) 兩個參數
      const { id, ...updates } = updatedTransaction;
      await updateTransaction(id, updates);
      setEditDialogOpen(false);
      setSelectedTransaction(null);
    } catch (error) {
      logger.component.error('更新交易失敗', error);
      // 錯誤處理由 AppContext 中的錯誤處理負責
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditDelete = async (transactionId: string) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      await deleteTransaction(transactionId);
      setEditDialogOpen(false);
      setSelectedTransaction(null);
    } catch (error) {
      logger.component.error('刪除交易失敗', error);
      // 錯誤處理由 AppContext 中的錯誤處理負責
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditDialogClose = () => {
    if (!isSubmitting) {
      setEditDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async () => {
    if (filteredTransactions.length === 0) return;

    const typeMap: Record<string, string> = {
      'buy': '買入',
      'sell': '賣出',
      'dividend': '股利',
      'stock_increase': '股票增加',
      'stock_decrease': '股票減少',
      'cash_deposit': '存入現金',
      'cash_withdrawal': '提取現金'
    };

    const text = filteredTransactions.map(transaction => {
      const transactionDate = new Date(transaction.transactionDate || transaction.timestamp);
      const dateStr = format(transactionDate, 'yyyy/MM/dd');
      const type = typeMap[transaction.type] || transaction.type;
      const stockName = getStockName(transaction.symbol, transaction.stockName);
      const subtotal = transaction.quantity * transaction.price;
      const totalCost = transaction.total;
      const notes = transaction.notes || '';

      if (['cash_deposit', 'cash_withdrawal'].includes(transaction.type)) {
        return `${dateStr} ${type} ${stockName} ${transaction.type === 'cash_deposit' ? '+' : '-'}$${Math.abs(totalCost).toLocaleString()} ${notes}`;
      }

      return `${dateStr} ${type} ${transaction.symbol} ${stockName} ${transaction.quantity}股 @$${transaction.price} = $${subtotal.toLocaleString()}`;
    }).join('\n');

    try {
      await navigator.clipboard.writeText(text);
      showSuccess('已複製到剪貼簿');
    } catch (error) {
      logger.component.error('複製到剪貼簿失敗', error);
      showError('複製失敗');
    }
  };

  // CSV Export function
  const exportToCSV = () => {
    const csvHeaders = [
      '成交日期',
      '交易類型',
      '股票代號',
      '股票名稱',
      '成交股數',
      '成交價',
      '成交價金',
      '手續費',
      '交易稅',
      '單次成本',
      '調整金額',
      '調整原因',
      '備註'
    ].join(',');

    const csvData = filteredTransactions.map(transaction => {
      const transactionDate = new Date(transaction.transactionDate || transaction.timestamp);
      const dateStr = format(transactionDate, 'yyyy/MM/dd');
      
      const typeMap: Record<string, string> = {
        'buy': '買入',
        'sell': '賣出',
        'dividend': '股利',
        'stock_increase': '股票增加',
        'stock_decrease': '股票減少',
        'cash_deposit': '存入現金',
        'cash_withdrawal': '提取現金'
      };
      
      const subtotal = transaction.quantity * transaction.price;
      const brokerageFee = transaction.fees || 0;
      const transactionTax = transaction.taxes || 0;
      const totalCost = transaction.total;
      
      return [
        dateStr,
        typeMap[transaction.type] || transaction.type,
        transaction.symbol,
        `"${getStockName(transaction.symbol, transaction.stockName).replace(/"/g, '""')}"`, // Escape quotes, 優先使用存儲的股票名稱
        transaction.quantity,
        transaction.price,
        subtotal,
        brokerageFee,
        transactionTax,
        Math.abs(totalCost),
        transaction.adjustmentAmount || 0,
        `"${transaction.adjustmentReason || ''}"`,
        `"${transaction.notes || ''}"`
      ].join(',');
    }).join('\n');

    const csvContent = '\uFEFF' + csvHeaders + '\n' + csvData; // Add BOM for UTF-8
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const now = new Date();
    const filename = `交易紀錄_${format(now, 'yyyy-MM-dd_HHmm')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // UI-10:釋放 objectURL,避免記憶體洩漏
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="交易紀錄"
        icon={Receipt}
        subtitle={`查看您的所有交易記錄 • ${stats.totalTransactions}筆交易`}
        actions={null}
      />

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3, 
        mb: 4,
        '& > *': {
          flex: '1 1 280px',
          minWidth: '280px'
        }
      }}>
        <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Receipt sx={{ fontSize: 28, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">總交易次數</Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {stats.totalTransactions}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              筆交易
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ShoppingCart sx={{ fontSize: 28, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">買入金額</Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              ${stats.totalBought.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {stats.buyCount} 筆買入
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Sell sx={{ fontSize: 28, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">賣出金額</Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              ${stats.totalSold.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {stats.sellCount} 筆賣出
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: stats.netAmount >= 0 
            ? 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
            : 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)', 
          color: 'white' 
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MonetizationOn sx={{ fontSize: 28, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">淨額</Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {stats.netAmount >= 0 ? '+' : ''}${stats.netAmount.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {stats.netAmount >= 0 ? '獲利' : '虧損'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            篩選交易記錄
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="複製">
              <IconButton
                onClick={copyToClipboard}
                disabled={filteredTransactions.length === 0}
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
                  },
                }}
              >
                <ContentCopy />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={copyToClipboard}
              disabled={filteredTransactions.length === 0}
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                minWidth: '100px',
                background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
                },
              }}
            >
              複製
            </Button>
            <Tooltip title="匯出 CSV">
              <IconButton
                onClick={exportToCSV}
                disabled={filteredTransactions.length === 0}
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                  },
                }}
              >
                <FileDownload />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={exportToCSV}
              disabled={filteredTransactions.length === 0}
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                minWidth: '120px',
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                },
              }}
            >
              匯出 CSV
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
          <Box sx={{ flex: '2 1 300px', minWidth: '300px' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="搜尋股票代號..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                sx: { fontSize: '16px' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1565c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1565c0',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <FormControl fullWidth>
              <InputLabel>交易類型</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease')}
                label="交易類型"
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="buy">買入</MenuItem>
                <MenuItem value="sell">賣出</MenuItem>
                <MenuItem value="dividend">股利</MenuItem>
                <MenuItem value="cash_deposit">存入現金</MenuItem>
                <MenuItem value="cash_withdrawal">提取現金</MenuItem>
                <MenuItem value="stock_increase">股票增加</MenuItem>
                <MenuItem value="stock_decrease">股票減少</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Transaction Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel
                    active={sortField === 'transactionDate'}
                    direction={sortField === 'transactionDate' ? sortDirection : 'asc'}
                    onClick={() => handleSort('transactionDate')}
                  >
                    成交日期
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  交易類別
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel
                    active={sortField === 'symbol'}
                    direction={sortField === 'symbol' ? sortDirection : 'asc'}
                    onClick={() => handleSort('symbol')}
                  >
                    股票名稱
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel
                    active={sortField === 'quantity'}
                    direction={sortField === 'quantity' ? sortDirection : 'asc'}
                    onClick={() => handleSort('quantity')}
                  >
                    成交股數
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  成交價
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  成交價金
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  手續費
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  交易稅
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel
                    active={sortField === 'total'}
                    direction={sortField === 'total' ? sortDirection : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    單次成本
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '100px' }}>
                  操作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((transaction) => {
                // 直接使用記錄的數據
                const subtotal = transaction.quantity * transaction.price;
                const brokerageFee = transaction.fees || 0; // 使用記錄的手續費
                const transactionTax = transaction.taxes || 0; // 使用記錄的稅金
                const totalCost = transaction.total; // 直接使用記錄的總成本
                  
                return (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                      height: '60px',
                    }}
                  >
                    {/* 成交日期 */}
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {format(transaction.transactionDate || transaction.timestamp, 'yyyy/MM/dd')}
                      </Typography>
                    </TableCell>
                    {/* 交易類別 */}
                    <TableCell align="center">
                      <Chip
                        icon={
                          transaction.type === 'buy' ? <ShoppingCart /> : 
                          transaction.type === 'sell' ? <Sell /> :
                          transaction.type === 'dividend' ? <MonetizationOn /> :
                          transaction.type === 'stock_increase' ? <Add /> :
                          transaction.type === 'stock_decrease' ? <Delete /> :
                          ['cash_deposit', 'cash_withdrawal'].includes(transaction.type) ? <AccountBalanceWallet /> :
                          <MonetizationOn />
                        }
                        label={
                          transaction.type === 'buy' ? '買入' : 
                          transaction.type === 'sell' ? '賣出' :
                          transaction.type === 'dividend' ? '股利' :
                          transaction.type === 'stock_increase' ? '股票增加' :
                          transaction.type === 'stock_decrease' ? '股票減少' :
                          transaction.type === 'cash_deposit' ? '存入資金' :
                          transaction.type === 'cash_withdrawal' ? '提取資金' :
                          '其他'
                        }
                        color={
                          transaction.type === 'buy' ? 'success' : 
                          transaction.type === 'sell' ? 'error' :
                          transaction.type === 'dividend' ? 'warning' :
                          transaction.type === 'stock_increase' ? 'info' :
                          transaction.type === 'stock_decrease' ? 'error' :
                          transaction.type === 'cash_deposit' ? 'success' :
                          transaction.type === 'cash_withdrawal' ? 'error' :
                          'default'
                        }
                        size="medium"
                        sx={{ 
                          fontWeight: 'bold',
                          ...(transaction.type === 'dividend' && {
                            backgroundColor: '#ff9800',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }),
                          ...(transaction.type === 'stock_increase' && {
                            backgroundColor: '#4caf50',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }),
                          ...(transaction.type === 'stock_decrease' && {
                            backgroundColor: '#f44336',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }),
                          ...(transaction.type === 'cash_deposit' && {
                            backgroundColor: '#4caf50',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }),
                          ...(transaction.type === 'cash_withdrawal' && {
                            backgroundColor: '#f44336',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          })
                        }}
                      />
                    </TableCell>
                    {/* 股票名稱 */}
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {transaction.symbol}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getStockName(transaction.symbol, transaction.stockName)}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* 成交股數 */}
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium">
                        {transaction.quantity.toLocaleString()}
                      </Typography>
                    </TableCell>
                    {/* 成交價 */}
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium">
                        ${transaction.price}
                      </Typography>
                    </TableCell>
                    {/* 成交價金 */}
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        ${subtotal.toLocaleString()}
                      </Typography>
                    </TableCell>
                    {/* 手續費 */}
                    <TableCell align="right">
                      <Typography variant="body1" color={brokerageFee > 0 ? "error.main" : "text.secondary"}>
                        {brokerageFee > 0 ? `$${brokerageFee.toLocaleString()}` : '-'}
                      </Typography>
                    </TableCell>
                    {/* 交易稅 */}
                    <TableCell align="right">
                      <Typography variant="body1" color={transactionTax > 0 ? "error.main" : "text.secondary"}>
                        {transactionTax > 0 ? `$${transactionTax.toLocaleString()}` : '-'}
                      </Typography>
                    </TableCell>
                    {/* 單次成本 */}
                    <TableCell align="right">
                      {transaction.adjustmentAmount && transaction.adjustmentAmount !== 0 ? (
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="body2">
                                記錄金額: ${Math.abs(transaction.total).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color={transaction.adjustmentAmount >= 0 ? 'success.light' : 'error.light'}>
                                調整: {transaction.adjustmentAmount >= 0 ? '+' : ''}${transaction.adjustmentAmount.toLocaleString()}
                              </Typography>
                              {transaction.adjustmentReason && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
                                  {transaction.adjustmentReason}
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Typography 
                            variant="subtitle1" 
                            fontWeight="bold"
                            color={transaction.type === 'buy' ? 'error.main' : 'success.main'}
                            sx={{ cursor: 'help' }}
                          >
                            {transaction.type === 'buy' ? '-' : '+'}${Math.abs(totalCost).toLocaleString()}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography 
                          variant="subtitle1" 
                          fontWeight="bold"
                          color={transaction.type === 'buy' ? 'error.main' : 'success.main'}
                        >
                          {transaction.type === 'buy' ? '-' : '+'}${Math.abs(totalCost).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>
                    {/* 操作 */}
                    <TableCell align="center">
                      <Tooltip title="編輯交易記錄">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(transaction)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredTransactions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm || typeFilter !== 'all' ? '沒有符合條件的交易紀錄' : '暫無交易紀錄'}
          </Typography>
        </Box>
      )}

      {/* 編輯交易對話框 */}
      {selectedTransaction && (
        <EditTransactionDialog
          open={editDialogOpen}
          transaction={selectedTransaction}
          onClose={handleEditDialogClose}
          onSave={handleEditSave}
          onDelete={handleEditDelete}
          isSubmitting={isSubmitting}
        />
      )}
    </Box>
  );
};

export default TransactionHistory;
